"use client";

import { BlogPost } from "@/app/actions/blogs";
import { Category } from "@/app/actions/categories";
import AuthorLink from "@/components/blog/AuthorLink";
import { FadeIn, FadeInStaggerItem, Stagger } from "@/components/ui/motion";
import TiltCard from "@/components/ui/TiltCard";
import Link from "next/link";
import { useState } from "react";

interface BlogListingProps {
    initialPosts: BlogPost[];
    categories: Category[];
}

export default function BlogListing({ initialPosts, categories }: BlogListingProps) {
    const [activeCategory, setActiveCategory] = useState<string>("all");

    const filteredPosts = activeCategory === "all"
        ? initialPosts
        : initialPosts.filter(post => post.categoryId === activeCategory);

    return (
        <div className="space-y-12">
            {/* Category Filter */}
            <FadeIn>
                <div className="flex flex-wrap gap-3 mb-12">
                    <button
                        onClick={() => setActiveCategory("all")}
                        className={`px-5 py-2 rounded-xl text-xs font-bold font-mono tracking-widest uppercase transition-all border ${activeCategory === "all"
                            ? "bg-black text-white border-black"
                            : "bg-white/40 backdrop-blur-md text-gray-500 border-white/60 hover:bg-white/60 hover:text-black"
                            }`}
                    >
                        [ All ]
                    </button>
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            onClick={() => setActiveCategory(category.id)}
                            className={`px-5 py-2 rounded-xl text-xs font-bold font-mono tracking-widest uppercase transition-all border ${activeCategory === category.id
                                ? "bg-black text-white border-black"
                                : "bg-white/40 backdrop-blur-md text-gray-500 border-white/60 hover:bg-white/60 hover:text-black"
                                }`}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>
            </FadeIn>

            {filteredPosts.length > 0 ? (
                <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredPosts.map((post, i) => (
                        <FadeInStaggerItem key={post.id} className="h-full">
                            <Link href={`/blogs/${post.categorySlug || 'uncategorized'}/${post.slug}`} className="block h-full group">
                                <TiltCard
                                    className="relative bg-white/40 backdrop-blur-md border border-white/60 p-6 shadow-sm group-hover:shadow-xl group-hover:bg-white/60 transition-all duration-300 h-full rounded-2xl"
                                    innerClassName="h-full flex flex-col"
                                >
                                    {/* Step Indicator */}
                                    <div className="absolute top-4 right-5 text-[10px] font-mono font-bold text-black/30 tracking-widest z-20">
                                        {(i + 1).toString().padStart(2, '0')}
                                    </div>

                                     <div className="aspect-[16/10] mb-6 relative">
                                        {post.coverImage && (
                                            <div className="absolute inset-0 transform scale-95 translate-y-3 blur-xl opacity-60 z-0 pointer-events-none transition-transform duration-300 group-hover:translate-y-4">
                                                <img src={post.coverImage} alt="" className="w-full h-full object-cover rounded-xl" />
                                            </div>
                                        )}
                                        <div className="w-full h-full bg-gray-100 relative overflow-hidden rounded-xl border border-black/5 shadow-inner z-10">
                                            {post.coverImage ? (
                                                <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-700" />
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs font-mono uppercase tracking-widest">No Cover</div>
                                            )}
                                            {post.categoryName && (
                                                <div className="absolute top-4 left-4 z-10">
                                                    <span className="bg-white/80 backdrop-blur-md text-black px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest border border-white">
                                                        {post.categoryName}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="h-px w-4 bg-black/20" />
                                        <div className="text-[10px] font-mono font-bold text-gray-400 tracking-widest uppercase">
                                            {post.publishedAt}
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-bold mb-3 tracking-tight group-hover:text-black transition-colors leading-snug">
                                        {post.title}
                                    </h3>

                                    <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-1 line-clamp-3 font-medium">
                                        {post.excerpt}
                                    </p>

                                    <div className="flex justify-between items-center border-t border-black/5 pt-5 mt-auto">
                                        <div className="min-w-0">
                                            <AuthorLink
                                                author={post.author}
                                                authorUrl={post.authorUrl}
                                                authorPhoto={post.authorPhoto}
                                                className="text-[11px] font-mono font-bold text-gray-400 hover:text-black uppercase tracking-wider transition-colors"
                                            />
                                        </div>
                                        <div className="flex-shrink-0 ml-4">
                                            <span className="text-[10px] font-bold font-mono tracking-widest uppercase border-b border-transparent group-hover:border-black pb-0.5 transition-all inline-block whitespace-nowrap">
                                                Read Article _
                                            </span>
                                        </div>
                                    </div>
                                </TiltCard>
                            </Link>
                        </FadeInStaggerItem>
                    ))}
                </Stagger>
            ) : (
                <FadeIn>
                    <div className="text-center py-20 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                        <p className="text-gray-500 font-medium">No posts found in this category.</p>
                    </div>
                </FadeIn>
            )}
        </div>
    );
}
